import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Slider,
  TextField,
  Typography
} from '@mui/material';
import {
  BundlePollQuestion,
  PollQuestion,
  PollResponse,
  PollTemplate,
  RangePollQuestion,
  ResponseValue,
  SelectPollQuestion
} from '../../types';
import React, { useEffect, useState } from 'react';
import { Timestamp, collection, doc, getDoc, setDoc } from 'firebase/firestore';
import { useNavigate, useParams } from 'react-router-dom';

import PollVoterSelectorDialog from './PollVoterSelectorDialog';
import { db } from '../../firebase';
import useAuth from "../../hooks/useAuth";
import { v4 as uuidv4 } from 'uuid';

interface Responses {
  [question: string]: ResponseValue;
}

const PollQuestionnaire: React.FC = () => {
  const { user } = useAuth();
  const { id } = useParams<{ id: string }>();

  const [pollTemplate, setPollTemplate] = useState<PollTemplate | null>(null);
  const [responses, setResponses] = useState<Responses>({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [warningDialogOpen, setWarningDialogOpen] = useState(false);
  const [unansweredQuestions, setUnansweredQuestions] = useState<string[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPollTemplate = async () => {
      if (id) {
        try {
          const pollRef = doc(db, 'polls', id);
          const pollSnap = await getDoc(pollRef);
          
          if (pollSnap.exists()) {
            setPollTemplate(pollSnap.data() as PollTemplate);
          } else {
            setError('Poll template not found');
          }
        } catch (error) {
          console.error('Error fetching poll template:', error);
          setError('Failed to fetch poll template. Please try again later.');
        } finally {
          setLoading(false);
        }
      }
    };
    fetchPollTemplate();
  }, [id]);

  const handleInputChange = (question: string, value: ResponseValue) => {
    setResponses(prev => ({
      ...prev,
      [question]: value
    }));
  };

  const findUnansweredQuestions = (): string[] => {
    if (!pollTemplate) return [];

    return pollTemplate.questions
      .filter(question => {
        // it's ok not to provide answer to a bundle question
        if (question.type === 'bundle') return false;

        const response = responses[question.text];
        
        // Check different response types
        if (response === undefined || response === null) return true;
        
        // For array responses (like checkboxes)
        if (Array.isArray(response) && response.length === 0) return true;
        
        // For string responses
        if (typeof response === 'string' && response.trim() === '') return true;
        
        return false;
      })
      .map(question => question.text);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!user) {
      setError('You must be logged in to submit a poll response.');
      return;
    }

    const unAnswered = findUnansweredQuestions();
    
    if (unAnswered.length > 0) {
      setUnansweredQuestions(unAnswered);
      setWarningDialogOpen(true);
      return;
    }

    await submitPollResponse();
  };

  const submitPollResponse = async () => {
    try {
      const responseId = uuidv4();
      const pollResponse: PollResponse = {
        id: responseId,
        pollId: id as string,
        pollTitle: pollTemplate?.title as string,
        responses: responses,
        submittedAt: Timestamp.now(),
        submittedBy: user?.display_name as string,
      };
      
      const responseRef = doc(collection(db, 'pollResponses'), responseId);
      await setDoc(responseRef, pollResponse);
      
      navigate('/polls');
    } catch (error) {
      console.error('Error submitting poll response: ', error);
      setError('Error submitting poll response: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  const handleWarningDialogClose = (confirmed: boolean) => {
    setWarningDialogOpen(false);
    if (confirmed) {
      submitPollResponse();
    }
  };

  const renderQuestion = (question: PollQuestion) => {
    switch (question.type) {
      case 'select':
      case 'multiselect':
        return renderSelectQuestion(question as SelectPollQuestion);
      case 'range':
        return renderRangeQuestion(question as RangePollQuestion);
      case 'text':
        return renderTextQuestion(question);
      case 'voter':
        return <PollVoterSelectorDialog pollId={id as string} question={question} handleInputChange={handleInputChange} />;
      case 'bundle':
        return renderBundleQuestion(question as BundlePollQuestion);
      default:
        return null;
    }
  };

  const handleBundleInputChange = (question: string, value: ResponseValue) => {
    setResponses(prev => ({
      ...prev,
      [question]: value
    }));

    // also set the corresponding qna responses
    const bundleQuestion = pollTemplate?.questions.find(q => q.type === 'bundle' && q.text === question) as BundlePollQuestion;
    if (bundleQuestion?.qnas) {
      const qnas = bundleQuestion?.qnas;
      Object.keys(qnas).forEach(option => {
        if (option === value) {
          Object.keys(qnas[option]).forEach(q => {
            setResponses(prev => ({
              ...prev,
              [q]: qnas[option][q]
            }))
          })
        }
      });
    }
  };

  const renderBundleQuestion = (question: BundlePollQuestion) => {
    return (
      <FormControl component="fieldset" margin="normal" fullWidth>
        <Typography variant="subtitle2">{question.text}</Typography>
        <RadioGroup
          value={responses[question.text] as string || ''}
          onChange={(e) => handleBundleInputChange(question.text, e.target.value)}
        >
          {Object.keys(question.qnas).sort().map((option, index) => (
            <FormControlLabel 
              key={index} 
              value={option} 
              control={<Radio />} 
              label={option} 
            />
          ))}
        </RadioGroup>
      </FormControl>
    );
  };

  const renderSelectQuestion = (question: SelectPollQuestion) => {
    if (question.controlType === 'radio') {
      return (
        <FormControl component="fieldset" margin="normal" fullWidth>
          <Typography variant="subtitle2">{question.text}</Typography>
          <RadioGroup
            value={responses[question.text] as string || ''}
            onChange={(e) => handleInputChange(question.text, e.target.value)}
          >
            {question.options.map((option, index) => {
              const optionValue = option.replace(/^["']|["']$/g, '');
              return <FormControlLabel key={index} value={optionValue} control={<Radio />} label={optionValue} />
            })}
          </RadioGroup>
        </FormControl>
      );
    } else if (question.controlType === 'dropdown') {
      return (
        <FormControl fullWidth margin="normal">
          <InputLabel>{question.text}</InputLabel>
          <Select
            value={responses[question.text] as string || ''}
            onChange={(e) => handleInputChange(question.text, e.target.value as string)}
          >
            {question.options.map((option, index) => {
              const optionValue = option.replace(/^["']|["']$/g, '');
              return <MenuItem key={index} value={optionValue}>{optionValue}</MenuItem>
            })}
          </Select>
        </FormControl>
      );
    } else if (question.controlType === 'checkbox') {
      return (
        <FormControl component="fieldset" margin="normal" fullWidth>
          <Typography variant="subtitle2">{question.text}</Typography>
          <FormGroup>
            {question.options.map((option, index) => {
              const optionValue = option.replace(/^["']|["']$/g, '');
              return (<FormControlLabel
                key={index}
                control={
                  <Checkbox
                    checked={(responses[question.text] as string[] || []).includes(optionValue)}
                    onChange={(e) => {
                      const currentResponses = responses[question.text] as string[] || [];
                      const newResponses = e.target.checked
                        ? [...currentResponses, optionValue]
                        : currentResponses.filter(item => item !== optionValue);
                      handleInputChange(question.text, newResponses);
                    }}
                  />
                }
                label={optionValue}
              />);
            })}
          </FormGroup>
        </FormControl>
      );
    }
  };

  const renderRangeQuestion = (question: RangePollQuestion) => {
    return (
      <Box sx={{ my: 2 }}>
        <Typography variant="subtitle2">{question.text}</Typography>
        <Slider
          marks
          min={question.min}
          max={question.max}
          step={1}
          valueLabelDisplay="auto"
          value={responses[question.text] as number || question.min}
          onChange={(_, value) => handleInputChange(question.text, value as number)}
        />
      </Box>
    );
  };

  const renderTextQuestion = (question: PollQuestion) => {
    return (
      <TextField
        fullWidth
        label={question.text}
        multiline
        rows={4}
        margin="normal"
        value={responses[question.text] as string || ''}
        onChange={(e) => handleInputChange(question.text, e.target.value)}
      />
    );
  };

  if (loading) {
    return <Typography>Loading...</Typography>;
  }

  if (!pollTemplate) {
    return <Typography color="error">Failed to load poll template. Please try again later.</Typography>;
  }

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 600, margin: 'auto' }}>
      <Typography variant="h4" align="center">{pollTemplate.title}</Typography>
      <Typography variant="body1">{pollTemplate.description}</Typography>
      {error && <Typography color="error">{error}</Typography>}

      {pollTemplate.questions.map((question) => (
        <Box key={question.id}>
          {renderQuestion(question)}
        </Box>
      ))}

      <Button type="submit" variant="contained">
        Submit Poll Response
      </Button>

      <Dialog
        open={warningDialogOpen}
        onClose={() => handleWarningDialogClose(false)}
        aria-labelledby="unanswered-questions-dialog"
      >
        <DialogTitle id="unanswered-questions-dialog">Unanswered Questions</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The following questions have not been answered:
            <ul>
              {unansweredQuestions.map((q, index) => (
                <li key={index}>{q}</li>
              ))}
            </ul>
            Do you want to continue and submit the poll response?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleWarningDialogClose(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={() => handleWarningDialogClose(true)} color="primary" autoFocus>
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PollQuestionnaire;
